import { FC, useState } from 'react'

import { useTranslation } from 'react-i18next'
import * as Layout from 'views/layouts/NewLayout'
import * as Header from 'views/ui/Header/Header'

import { AntdTabsProps, Icons, Progress, useHelmet } from '@agro-club/agroclub-shared'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { DetailedCarrier } from 'modules/domain/logisticsKR/types'
import { endpoints } from 'modules/endpoints'
import { getPersonalNumberLabel } from 'modules/utils/numbers/formatPersonalNumber'
import { useParams } from 'react-router-dom'
import { AAntdTabs, AButton } from 'views/components/Analytics'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import ItemLoadingLayout from 'views/layouts/ItemLoadingLayout/ItemLoadingLayout'
import { TwoColumns } from 'views/styled/detailedPage'
import APhone from 'views/ui/Phone/APhone'
import { AnalyticsPages, AnalyticsPlaces } from '../../analyticsPlaces'
import { HeaderButtons } from '../../components/styled'
import { CarrierEditModal } from '../CarrierEditModal/CarrierEditModal'
import { CarriersKRAddCarModal } from '../CarriersKRAddCar/CarriersKRAddCarModal'
import CustomersKRRoutes from '../routes'
import CarrierAccordion from './CarrierAccordion/CarrierAccordion'
import { CarrierBoard } from './CarrierBoard/CarrierBoard'
import { LogisticsVerifiedIcon } from '../LogisticsVerifiedIcon'
import useFormatDate from 'hooks/useFormatDate'
import styled from 'styled-components'
import { LogisticsAgentVerifiedIcon } from '../LogisticsAgentVerifiedIcon'
import { Drivers } from './Drivers/Drivers'
import { useDrivers } from './useDrivers'
import { useTrailers } from './useTrailers'
import { Trailers } from './Trailers/Trailers'
import { useCars } from './useCars'
import { Cars } from './Cars/Cars'

export const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`
export const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
`

const TabContainer = styled.div`
  height: calc(100dvh - 195px);
  overflow: auto;
`

enum CarrierTabs {
  main = 'main',
  drivers = 'drivers',
  cars = 'cars',
  trailers = 'trailers',
}

const CarrierKRDetails: FC = () => {
  const { t } = useTranslation('logisticsKR')
  const { id } = useParams()
  const formatDate = useFormatDate()
  const [fetchProgress, carrier, refetch] = useSingleEntity<DetailedCarrier>(endpoints.LogisticsKR.carriers(id))
  const [editCarrierModal, setEditCarrierModal] = useState<boolean>(false)
  const [addCarModal, setAddCarModal] = useState(false)
  useHelmet({ title: carrier?.name || '' })
  const [activeTab, setActiveTab] = useState<CarrierTabs>(CarrierTabs.main)
  const parsedId = parseInt(id)
  const driversData = useDrivers(parsedId)
  const carsData = useCars(parsedId)
  const trailersData = useTrailers(parsedId)

  if (fetchProgress === Progress.WORK || !carrier) return <ItemLoadingLayout id={id as string} />

  return (
    <APageWrapper page={AnalyticsPages.CARRIERS.DETAIL} place={AnalyticsPlaces.CARRIERS.DETAIL} id={id}>
      <Layout.WrapperContentFlex>
        <Layout.Header>
          <Layout.TopHeader>
            <Header.Breadcrumbs
              routes={[
                { path: CustomersKRRoutes.List, breadcrumbName: t('carriers.heading') },
                { breadcrumbName: carrier.name ?? carrier.id },
              ]}
            />
            <HeaderButtons>
              <AButton id="edit" color="secondary" size="medium" onClick={() => setEditCarrierModal(true)}>
                {t('common:edit')}
              </AButton>
              <Header.LineSeparator />
              <AButton id="addCar" Icon={Icons.IconAdd} color="approve" onClick={() => setAddCarModal(true)}>
                {t('carriers.carsAdd.heading')}
              </AButton>
            </HeaderButtons>
          </Layout.TopHeader>
          {editCarrierModal && (
            <CarrierEditModal onClose={() => setEditCarrierModal(false)} carrier={carrier} onSuccess={refetch} />
          )}
          {addCarModal && (
            <CarriersKRAddCarModal carrierId={carrier.id} onSuccess={refetch} onClose={() => setAddCarModal(false)} />
          )}
          <Layout.PageTitleWrapper>
            <TitleWrapper>
              <Header.Title compact title={carrier.name} margin="0" />
              <IconsWrapper>
                {carrier.verified_by_security_at && (
                  <LogisticsVerifiedIcon
                    isVerified={!!carrier.verified_by_security_at}
                    hintText={`${t('carriers.verifiedBySecurity')} ${formatDate(carrier.verified_by_security_at)}`}
                  />
                )}
                {carrier.verified_as_agent_by_security_at && (
                  <LogisticsAgentVerifiedIcon
                    isVerified={!!carrier.verified_as_agent_by_security_at}
                    hintText={`${t('carriers.verifiedBySecurityAsAgent')} ${formatDate(
                      carrier.verified_as_agent_by_security_at,
                    )}`}
                  />
                )}
              </IconsWrapper>
            </TitleWrapper>
            <Header.SubTitle>
              {carrier.inn && `${getPersonalNumberLabel()}: ${carrier.inn}`}
              {carrier.contact_name && ` / ${carrier.contact_name}`}
              {carrier.phone && (
                <>
                  {` / `}
                  <APhone phone={carrier.phone} />
                </>
              )}
              {' / '}
              {carrier.carrier_role.map((role) => t(`carrierRoles.${role}`)).join(', ')}
              {' / '}
              {t(`carriers.${carrier.carrier_type}`)}
            </Header.SubTitle>
          </Layout.PageTitleWrapper>
        </Layout.Header>
        <AAntdTabs
          id="tabs"
          items={
            [
              {
                //count: activeBranch.offers_count,
                label: t('Главная'),
                key: CarrierTabs.main,
                children: (
                  <TabContainer>
                    <TwoColumns>
                      <CarrierBoard carrier={carrier} />
                      <CarrierAccordion carrier={carrier} />
                    </TwoColumns>
                  </TabContainer>
                ),
              },
              {
                label: t('Водители'),
                key: CarrierTabs.drivers,
                count: driversData.countForDisplay,
                children: (
                  <TabContainer>
                    <Drivers data={driversData} carrierId={id} />
                  </TabContainer>
                ),
              },
              {
                label: t('Машины'),
                key: CarrierTabs.cars,
                count: carsData.countForDisplay,
                children: (
                  <TabContainer>
                    <Cars data={carsData} carrierId={id} />
                  </TabContainer>
                ),
              },
              {
                label: t('Прицепы'),
                key: CarrierTabs.trailers,
                count: trailersData.countForDisplay,
                children: (
                  <TabContainer>
                    <Trailers data={trailersData} carrierId={id} />
                  </TabContainer>
                ),
              },
            ] as AntdTabsProps['items']
          }
          onChange={(key) => setActiveTab(key as CarrierTabs)}
          activeKey={activeTab}
        />
      </Layout.WrapperContentFlex>
    </APageWrapper>
  )
}

export default CarrierKRDetails
